/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { PropertyGroupAssignment, GroupPropertyAssignment, OrganizationDetailsForm } from './Partials'
import { ActionButton, StyledHeader, StyledMetaData } from 'components/widgets'
import { TypeToConfirmDialog } from 'components/partials'
import { ALL_ORGS_SELECTED, getAppColors } from 'lib/constants'
import { Organization, trans, TranslationGroup, TranslationKey, ucfirst } from 'lib/types'
import { ChevronLeft } from '@material-ui/icons'
import { NavState, Orgs } from 'models'
import { isSuperAdmin } from 'lib/utils/auth'
import { useAdmin } from 'contexts/AdminProvider'
import { useGlobal } from 'contexts/GlobalProvider'
interface EditOrgViewProps {
  org: Organization
}

export const EditOrgView = ({ org }: EditOrgViewProps) => {
  const translation: TranslationGroup = trans.merge(TranslationKey.ADMIN)
  const orgsTranslation: TranslationGroup = trans.merge(TranslationKey.ORGS)
  const { setSelectedAdminOrgId } = useAdmin()
  const { setSelectedOrg, setSelectedOrgId } = useGlobal()

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const orgIsGroup = Orgs.isGroup(org)
  const orgLabel = orgIsGroup ? orgsTranslation.group : orgsTranslation.property

  // Leaving the page is the same clearing the back link does — but here it is
  // mandatory rather than optional, because the org this view is built around no
  // longer exists and every child section would refetch against a dead id.
  function clearSelection() {
    setSelectedAdminOrgId(ALL_ORGS_SELECTED)
    setSelectedOrg(null)
    setSelectedOrgId(ALL_ORGS_SELECTED)
  }

  function handleDelete() {
    setConfirmingDelete(false)
    setDeleting(true)
    // `Orgs.deleteOrg` notifies and rethrows on failure, so the selection is only
    // cleared once the row is actually gone — a failed delete leaves the operator
    // on the property they were trying to remove.
    Orgs.deleteOrg(org.id)
      .then(() => {
        // The context setters below only move React state. The id is now dangling,
        // so the persisted copy has to go too: `Devices`, `Beacons`, `Responders`
        // and `AlertSessions` all read `selected_org_id` straight out of
        // localStorage, and would scope their next refetch to a deleted org.
        // `selectOrganization` commits the NavState store and localStorage together.
        NavState.selectOrganization(ALL_ORGS_SELECTED)
        clearSelection()
      })
      .catch(() => null)
      .finally(() => setDeleting(false))
  }

  return (
    <>
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1',
          }}
        >
          <StyledHeader>{org.name}</StyledHeader>
          <StyledMetaData>id: {org.id}</StyledMetaData>
        </div>
        <div css={{ display: 'flex', flexDirection: 'column', padding: '13px' }}>
          <div
            css={{
              display: 'flex',
              color: getAppColors('--color-brand'),
              cursor: 'pointer',
              justifyContent: 'flex-end',
              ':hover': { textDecoration: 'underline' },
            }}
            onClick={clearSelection}
          >
            <ChevronLeft css={{ margin: 'auto 0' }} />
            <div css={{ margin: 'auto 0' }}>
              {translation.back_to_all} {orgIsGroup ? translation.groups : translation.properties}
            </div>
          </div>
        </div>
      </div>

      <OrganizationDetailsForm organization={org} editAsGroup={orgIsGroup} />
      {orgIsGroup ? (
        <GroupPropertyAssignment selectedGroup={org} />
      ) : (
        <PropertyGroupAssignment selectedProperty={org} />
      )}

      {/* Last thing on the page, below every editable section: the only
          irreversible control here, kept well clear of the form's Apply row and
          of the back link so it is never the neighbour of a routine click. */}
      {isSuperAdmin() && (
        <div css={{ display: 'flex', justifyContent: 'flex-start', marginTop: '32px' }}>
          <ActionButton
            error
            height="28px"
            loading={deleting}
            disabled={deleting}
            onClick={() => setConfirmingDelete(true)}
          >
            {`${ucfirst(orgsTranslation.delete)} ${orgLabel}`}
          </ActionButton>
        </div>
      )}

      <TypeToConfirmDialog
        open={confirmingDelete}
        title={`${ucfirst(orgsTranslation.delete)} ${orgLabel}`}
        description={
          <>
            <div>
              {orgsTranslation.confirm_delete} <strong>{org.name}</strong>?
            </div>
            <div style={{ marginTop: '8px' }}>{orgsTranslation.delete_is_permanent}</div>
          </>
        }
        confirmPhrase={org.name}
        confirmLabel={ucfirst(orgsTranslation.delete)}
        onConfirm={handleDelete}
        handleClose={() => setConfirmingDelete(false)}
      />
    </>
  )
}
